import React, { useEffect, useMemo, useState } from 'react';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import { useQuery } from 'react-query';
import OrganizationService, { OrganizationProps } from 'services/hooks/organization-service';
import { Table } from '../Common/Table';
import Save from './OrgForms/save';
import Delete from './OrgForms/delete';
import { toast } from 'react-toastify';
import { generateApiFailErrorMessage } from 'utils/helpers';
import BuildingService, { BuildingProps } from 'services/hooks/building-service';
import IntegrationService, { IntegrationProps } from 'services/hooks/integration-service';
import FullScreenLoader from 'components/elements/Spinner/FullScreenLoader';
import { useAuth } from 'services/auth-service';
import { FilterSelect } from 'utils/FilterSelectUtils';

export default function OrgSaveMode() {
  const [getOrgsResult, setGetOrgsResult] = useState<OrganizationProps[] | null>([]);
  const {
    isLoading: isLoadingOrgs,
    refetch,
    data: orgData
  } = useQuery<OrganizationProps[], Error>('organizations', OrganizationService.findAllOrgs, {
    onError: () => {
      toast.error(generateApiFailErrorMessage('Organizations'));
    }
  });

  const { data: buildingsData } = useQuery<BuildingProps[], Error>(
    'buildings',
    BuildingService.findAllBuildings,
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage('Buildings'));
      }
    }
  );

  const { data: integrationsData } = useQuery<IntegrationProps[], Error>(
    'integrations',
    IntegrationService.findAllIntegrations,
    {
      onError: () => {
        toast.error(generateApiFailErrorMessage('Integrations'));
      }
    }
  );

  useEffect(() => {
    if (orgData && Array.isArray(orgData)) {
      setGetOrgsResult(orgData);
    }
  }, [orgData]);

  const {
    state: { user }
  } = useAuth();

  interface Column {
    id: string;
    Header: string;
    padding?: number;
    key?: string;
    accessor: any;
    Filter?: any;
    disableFilters?: boolean;
    disableSortBy?: boolean;
    Cell?: any;
    width?: number;
  }

  const columns = useMemo(() => {
    const cols: Column[] = [
      {
        id: 'name',
        Header: 'Name',
        accessor: 'name',
        disableFilters: true,
        disableSortBy: false
      }
    ];

    if (user.role === 'SUPER') {
      cols.push({
        id: 'account',
        Header: 'Account',
        accessor: (row: { buildingUuid: string }) => {
          return buildingsData?.find(
            (building: { uuid?: string }) => building?.uuid === row?.buildingUuid
          )?.accountName;
        },
        disableFilters: true,
        disableSortBy: false
      });
    }

    cols.push({
      id: 'building',
      Header: 'Building',
      accessor: (row: { buildingUuid: string }) => {
        return buildingsData?.find(
          (building: { uuid?: string }) => building?.uuid === row?.buildingUuid
        )?.name;
      },
      Filter: FilterSelect,
      disableSortBy: false
    });

    cols.push({
      id: 'integration',
      Header: 'Integration',
      accessor: (row: { integrationUuid: string }) => {
        const integration = integrationsData?.find(
          (integrationOne: { uuid?: string }) => integrationOne?.uuid === row?.integrationUuid
        );
        if (integration) {
          // Show integration name for 'bline' as 'B-Line' on UI
          return integration.name === 'bline' ? 'B-Line' : integration.name;
        }
        return 'None';
      },
      disableFilters: true,
      disableSortBy: false
    });

    cols.push({
      id: 'actions',
      Header: 'Actions',
      accessor: (data: any) => data,
      Cell: (row: any) => {
        return (
          <div className="flex gap-4">
            <SecondaryButton
              onClick={() => {
                row.setEditUUID(row.value.uuid);
                row.setUpdateShowModal(true);
              }}
              buttonLabel="Edit"
              variant="default"
              size="px-4 w-auto"
            />
            <SecondaryButton
              onClick={() => {
                row.setDeleteUUID(row.value.uuid);
                row.setDeleteShowModal(true);
              }}
              buttonLabel="Delete"
              variant="default"
              size="px-4 w-auto"
            />
          </div>
        );
      },
      width: 150,
      disableFilters: true
    });

    return cols;
  }, [buildingsData, integrationsData, user.role]);

  if (isLoadingOrgs || !getOrgsResult) {
    return <FullScreenLoader />;
  }

  return (
    <div className="organization">
      <Table
        name="Organizations"
        refetch={refetch}
        columns={columns}
        data={getOrgsResult}
        defaultSortBy={{ id: 'name', desc: false }}
        CreateForm={Save}
        DeleteForm={Delete}
        createQuery={OrganizationService.createOrg}
        updateQuery={OrganizationService.updateOrg}
        deleteQuery={OrganizationService.deleteOrgById}
        editQuery={OrganizationService.findOrgById}
        queryName="organizations"
        buttonName="Organization"
        toolTipMessage="Create and manage organizations. Multiple organizations can belong to the same building."
      />
    </div>
  );
}
